import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, BookOpen, TrendingUp, TrendingDown, Sparkles } from "lucide-react";
import Layout from "@/components/Layout";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const empty = { symbol: "", direction: "long", entry_price: "", exit_price: "", quantity: "1", strategy: "", emotion: "", notes: "" };

export default function Journal() {
  const [trades, setTrades] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [grading, setGrading] = useState(null);

  const load = () => api.get("/journal").then(({ data }) => setTrades(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const regrade = async (id) => {
    setGrading(id);
    try {
      const { data } = await api.post(`/journal/${id}/grade`);
      toast.success(`AI graded this trade: ${data.grade}`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setGrading(null); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.symbol || !form.entry_price) { toast.error("Symbol and entry price are required"); return; }
    setSaving(true);
    try {
      await api.post("/journal", {
        ...form,
        symbol: form.symbol.toUpperCase(),
        entry_price: parseFloat(form.entry_price),
        exit_price: form.exit_price ? parseFloat(form.exit_price) : null,
        quantity: parseFloat(form.quantity) || 1,
      });
      toast.success("Trade logged");
      setForm(empty); setOpen(false); load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/journal/${id}`); load(); toast.success("Deleted"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <BookOpen className="w-7 h-7 text-cyan-400" />
            <div><h1 className="font-display text-3xl font-bold tracking-tight">Trade Journal</h1>
              <p className="text-slate-400 text-sm">Log every trade. P&L and outcome are computed automatically.</p></div>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-trade-button" className="bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold">
                <Plus className="w-4 h-4 mr-1" /> Log Trade
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121824] border-white/10 text-white">
              <DialogHeader><DialogTitle>Log a Trade</DialogTitle>
                <DialogDescription className="text-slate-400">Enter your trade details. P&L, outcome and AI grade are computed automatically for closed trades.</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Symbol"><Input value={form.symbol} onChange={upd("symbol")} placeholder="AAPL" className="bg-white/5 border-white/10 font-mono-num" data-testid="trade-symbol-input" /></Field>
                <Field label="Direction">
                  <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                    <SelectTrigger className="bg-white/5 border-white/10" data-testid="trade-direction-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121824] border-white/10 text-white">
                      <SelectItem value="long">Long</SelectItem>
                      <SelectItem value="short">Short</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Entry price"><Input type="number" value={form.entry_price} onChange={upd("entry_price")} className="bg-white/5 border-white/10 font-mono-num" data-testid="trade-entry-input" /></Field>
                <Field label="Exit price"><Input type="number" value={form.exit_price} onChange={upd("exit_price")} placeholder="optional" className="bg-white/5 border-white/10 font-mono-num" data-testid="trade-exit-input" /></Field>
                <Field label="Quantity"><Input type="number" value={form.quantity} onChange={upd("quantity")} className="bg-white/5 border-white/10 font-mono-num" /></Field>
                <Field label="Strategy"><Input value={form.strategy} onChange={upd("strategy")} placeholder="Breakout" className="bg-white/5 border-white/10" /></Field>
                <Field label="Emotion"><Input value={form.emotion} onChange={upd("emotion")} placeholder="Calm" className="bg-white/5 border-white/10" /></Field>
                <Field label="Notes"><Input value={form.notes} onChange={upd("notes")} className="bg-white/5 border-white/10" /></Field>
              </div>
              <DialogFooter>
                <Button onClick={save} disabled={saving} data-testid="trade-journal-submit-button" className="bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold w-full">
                  {saving ? "Saving…" : "Save Trade"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="glass rounded-2xl overflow-hidden">
          {trades.length === 0 ? (
            <div className="p-12 text-center text-slate-500">No trades yet. Log your first trade to start tracking.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="trades-table">
                <thead className="bg-white/5 text-slate-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3">Symbol</th>
                    <th className="text-left px-4 py-3">Dir</th>
                    <th className="text-right px-4 py-3">Entry</th>
                    <th className="text-right px-4 py-3">Exit</th>
                    <th className="text-right px-4 py-3">P&L</th>
                    <th className="text-left px-4 py-3">Strategy</th>
                    <th className="text-left px-4 py-3">Outcome</th>
                    <th className="text-left px-4 py-3">AI Grade</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {trades.map((t) => (
                    <tr key={t.trade_id} className="border-t border-white/5 hover:bg-white/5">
                      <td className="px-4 py-3 font-mono-num font-semibold">{t.symbol}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 ${t.direction === "long" ? "text-emerald-400" : "text-red-400"}`}>
                          {t.direction === "long" ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{t.direction}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono-num">{t.entry_price}</td>
                      <td className="px-4 py-3 text-right font-mono-num">{t.exit_price ?? "—"}</td>
                      <td className={`px-4 py-3 text-right font-mono-num font-semibold ${t.pnl == null ? "text-slate-500" : t.pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                        {t.pnl == null ? "open" : (t.pnl >= 0 ? "+" : "") + t.pnl}
                      </td>
                      <td className="px-4 py-3 text-slate-400">{t.strategy || "—"}</td>
                      <td className="px-4 py-3">
                        {t.outcome ? <span className={`text-xs px-2 py-0.5 rounded-full ${t.outcome === "win" ? "bg-emerald-500/15 text-emerald-400" : t.outcome === "loss" ? "bg-red-500/15 text-red-400" : "bg-white/10 text-slate-400"}`}>{t.outcome}</span> : "—"}
                      </td>
                      <td className="px-4 py-3">
                        {t.grade ? (
                          <button onClick={() => setDetail(t)} data-testid={`grade-badge-${t.trade_id}`}
                            className={`text-xs font-bold px-2 py-0.5 rounded-full ${gradeColor(t.grade)}`}>{t.grade}</button>
                        ) : t.exit_price != null ? (
                          <button onClick={() => regrade(t.trade_id)} disabled={grading === t.trade_id} data-testid={`grade-button-${t.trade_id}`}
                            className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:underline disabled:opacity-50">
                            <Sparkles className="w-3 h-3" /> {grading === t.trade_id ? "Grading…" : "Grade"}
                          </button>
                        ) : <span className="text-slate-600 text-xs">open</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => remove(t.trade_id)} data-testid={`delete-trade-${t.trade_id}`} className="text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent className="bg-[#121824] border-white/10 text-white">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-cyan-400" /> AI Trade Grade</DialogTitle>
              <DialogDescription className="text-slate-400">An AI coach grade (A+ to F) with a short critique of this trade.</DialogDescription>
            </DialogHeader>
            {detail && (
              <div className="space-y-3" data-testid="grade-detail">
                <div className="flex items-center gap-3">
                  <span className={`text-2xl font-extrabold px-3 py-1 rounded-lg ${gradeColor(detail.grade)}`}>{detail.grade}</span>
                  <span className="font-mono-num text-slate-300">{detail.symbol} · {detail.direction}</span>
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">{detail.critique || "No critique available."}</p>
                <Button onClick={() => regrade(detail.trade_id)} variant="outline" className="border-white/15 text-slate-300 w-full" data-testid="regrade-button">Re-grade with AI</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

function gradeColor(g) {
  if (g === "A+" || g === "A") return "bg-emerald-500/15 text-emerald-400";
  if (g === "B") return "bg-cyan-500/15 text-cyan-400";
  if (g === "C") return "bg-amber-500/15 text-amber-400";
  return "bg-red-500/15 text-red-400";
}

function Field({ label, children }) {
  return (
    <div><label className="text-xs uppercase tracking-wider text-slate-400 mb-1 block">{label}</label>{children}</div>
  );
}
